import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import AccommodationRoomDetailsList from '../AccommodationRoomDetailsList'
import Clickable from 'components/Common/Clickable'
import { accommodationSameRoomTileGridChildEnforcer } from './AccommodationSameRoomTileGridBase'
import TextLink from 'LuxKit/TextLink'
import { mapRoomPackage } from '../roomPackageUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

export const StyledAccommodationSameRoomTileDetails = styled(Group)`
  grid-area: details;
  ${accommodationSameRoomTileGridChildEnforcer}
  padding: ${rem(20)} ${rem(20)} ${rem(12)} ${rem(20)};
  min-width: 0;

  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    padding-bottom: ${rem(20)};
  }
`

// Match a line that starts with two asterisks, as that implies we're dealing with Markdown. For
// example: "\n**Size:** 165m²\n\n**Sleeps:** 4 guests\n\n**Partial sea view**\n".
const BEDBANK_RICH_DESC_REGEX = /^\*\*/m

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage | App.Property.Room
  urgencyLabels?: ReactNode
  actions?: ReactNode
  roomCount: number
  onShowRoomDetails?: React.MouseEventHandler<HTMLButtonElement>
}

function AccommodationSameRoomTileDetails(props: PropsWithChildren<Props>) {
  const {
    actions,
    children,
    roomPackage,
    urgencyLabels,
    roomCount,
    onShowRoomDetails,
  } = props

  const intl = useIntl()

  const name = mapRoomPackage(
    roomPackage,
    propertyRoom => propertyRoom.name,
    hotelPkg => hotelPkg.roomType.group?.name ?? hotelPkg.roomType.name,
    bedbankPkg => bedbankPkg.name,
  )

  const description = mapRoomPackage(
    roomPackage,
    propertyRoom => propertyRoom.description,
    hotelPkg => hotelPkg.roomType.description,
    bedbankPkg => !BEDBANK_RICH_DESC_REGEX.test(bedbankPkg.description) ? bedbankPkg.description : undefined,
  )

  const viewRoomDetailsText = intl.formatMessage({
    id: 'AtMjqASmzW',
    defaultMessage: 'View room details',
    description: 'Link text to open the room details modal.',
  })

  const headingText = roomCount > 1
    ? intl.formatMessage(
        {
          id: 'sKm8lePKAL',
          defaultMessage: '{count}x {roomName}',
          description: 'Heading showing room count multiplier and room name (e.g. "2x Deluxe Suite").',
        },
        { count: roomCount, roomName: name },
      )
    : name

  return <StyledAccommodationSameRoomTileDetails direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Heading variant="heading4">{headingText}</Heading>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
    </VerticalSpacer>
    {description && <CSSBreakpoint min="tablet"><BodyText variant="medium">{description}</BodyText></CSSBreakpoint>}
    {onShowRoomDetails && <>
      <CSSBreakpoint max="tablet">
        <TextLink onClick={onShowRoomDetails} size="medium">{viewRoomDetailsText}</TextLink>
      </CSSBreakpoint>
      <Clickable onClick={onShowRoomDetails}>
        <AccommodationRoomDetailsList roomPackage={roomPackage} urgencyLabels={urgencyLabels} />
      </Clickable>
    </>}
    {!onShowRoomDetails && <AccommodationRoomDetailsList roomPackage={roomPackage} urgencyLabels={urgencyLabels} />}
    {children}
  </StyledAccommodationSameRoomTileDetails>
}

export default AccommodationSameRoomTileDetails
